import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Combobox, ComboboxList, ComboboxProvider } from '@ariakit/react';
import * as RadixSelect from '@radix-ui/react-select';
import isEqual from 'lodash/isEqual';
import { startTransition, useRef, useState } from 'react';
import { styled } from 'styletron-react';
import { sizes } from '../../../../const/sizes';
import { zIndexes } from '../../../../const/zIndexes';
import { useControllableState } from '../../../../hooks/useControllableState';
import { useFloatingSurfaceStyle } from '../../../../hooks/useFloatingSurfaceStyle';
import { Box } from '../../../layout/Box';
import { SearchInput } from '../../SearchInput/SearchInput';
import { SelectBase } from '../SelectBase';
import { SelectComboboxItem } from './SelectComboboxItem';
import { useComboboxMatches } from './useComboboxMatches';
const StyledContent = styled(RadixSelect.Content, (p) => ({
    ...p.$floatingSurfaceStyle,
    width: 'var(--radix-select-trigger-width)',
    minWidth: p.$contentMinWidth,
    maxHeight: `min(var(--radix-select-content-available-height), ${sizes.combobox.maxHeight})`,
    zIndex: zIndexes.select,
}));
const StyledComboboxList = styled(ComboboxList, {
    overflowY: 'auto',
});
export const SelectCombobox = ({ options, open: openPassed, onOpenChange, defaultOpen, value: valuePassed, onValueChange, defaultValue, filterOptions, contentMinWidth, searchPlaceholder, ...props }) => {
    const floatingSurfaceStyle = useFloatingSurfaceStyle();
    const [open = false, setOpen] = useControllableState({
        value: openPassed,
        setValue: onOpenChange,
        defaultValue: defaultOpen,
    });
    const [value, setValue] = useControllableState({
        value: valuePassed,
        setValue: onValueChange,
        defaultValue,
    });
    const [searchValue, setSearchValue] = useState('');
    const matches = useComboboxMatches({
        options,
        filterOptions,
        searchValue,
        value,
    });
    const previousMatchesRef = useRef([]);
    const previousComboboxItemsRef = useRef([]);
    const setComboboxValue = (comboBoxValue) => {
        startTransition(() => {
            setSearchValue(comboBoxValue);
        });
    };
    const onBlurCapture = (event) => {
        /*
        Ariakit's Combobox manually triggers a blur event on virtually
        blurred items, making them work as if they had actual DOM
        focus. These blur events might happen after the corresponding
        focus events in the capture phase, leading Radix Select to
        close the popover. This happens because Radix Select relies on
        the order of these captured events to discern if the focus was
        outside the element. Since we don't have access to the
        onInteractOutside prop in the Radix SelectContent component to
        stop this behavior, we can turn off Ariakit's behavior here.
        */
        event.preventDefault();
        event.stopPropagation();
    };
    if (!isEqual(previousMatchesRef.current, matches)) {
        previousComboboxItemsRef.current = matches.map((option, index) => {
            const isLast = index === options.length - 1;
            return (_jsx(SelectComboboxItem, { option: option, withDivider: !isLast }, option.value));
        });
    }
    previousMatchesRef.current = matches;
    return (_jsx(SelectBase, { ...props, open: open, value: value, onOpenChange: setOpen, onValueChange: setValue, children: _jsx(ComboboxProvider, { open: open, setOpen: setOpen, includesBaseElement: false, setValue: setComboboxValue, resetValueOnHide: true, children: _jsxs(StyledContent, { $contentMinWidth: contentMinWidth, $floatingSurfaceStyle: floatingSurfaceStyle, position: 'popper', children: [_jsx(Box, { spacing: '1/4', children: _jsx(Combobox, { placeholder: searchPlaceholder, className: 'combobox', render: _jsx(SearchInput, {}), autoSelect: true, onBlurCapture: onBlurCapture }) }), _jsx(StyledComboboxList, { children: previousComboboxItemsRef.current })] }) }) }));
};
